/**
 * One line, for everything the Tasks page just did — and the way back out of it.
 *
 * Before this there were two answers to "what happened?". A cell edit set an undo
 * line on the list; a row action — Claim, Start, "Assign to me & start" — set
 * nothing at all, on the list and on the board alike, so the card changed under the
 * pointer and nothing said so and nothing offered to put it back. Worse, the two did
 * not know about each other: a claim left the *previous* edit's "assignee updated.
 * Undo" standing, and pressing Undo then reverted a step from two actions ago.
 *
 * So the notice is the page's, not a view's, and there is exactly one of it:
 *
 *   - **A new action replaces the old notice.** Undo always means the last thing
 *     that happened, which is the only thing a reader can mean by it.
 *   - **The way back is the caller's own.** This holds a closure, because what
 *     reverses an action is a fact about that action — `release` for a claim, the
 *     previous state and assignee for a start, the previous value for a cell — and a
 *     table of them here would be a second state machine.
 *   - **Undo says so.** It used to run silently, which reads as a button that did
 *     nothing; now the line says "Undone" and goes quiet a few seconds later.
 *
 * `useTaskNotice()` answers a do-nothing notice where there is no provider, so a
 * `TaskList` drawn on its own — the gallery's triage story — still renders.
 *
 * ## Where it is, and what it says out loud
 *
 * The line is **docked** (`TaskDock`): at scroll 3,072 an edit put its own
 * confirmation 2,800px above the window, so the Undo for a change somebody had just
 * made was off screen in a page four thousand pixels tall. It sits at the bottom of
 * the viewport with the bulk bar, for as long as there is anything to say.
 *
 * It is also **announced**. The strip's own element is an `aria-live` region that is
 * always in the document, empty or not: a live region that arrives with its text is
 * a region several screen readers never read, and the one thing this line is for is
 * telling somebody what just changed under their pointer.
 *
 * And the row itself says so: `flashed` is the reference of whatever was last
 * written to, for about a third of a second, which is what tells a reader **which**
 * of forty rows the sentence at the bottom of the screen is about.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { NotSent, isApiProblem, isNetworkError, messageOf } from '../../../api/problem.js'
import { Button } from '../../../components/portal/Button.js'
import { Notice } from '../../../components/portal/Status.js'
import { changedWords } from '../../../components/studio/taskWords.js'

/** How long "Undone" stays up before the line goes quiet. */
const UNDONE_MS = 5_000

/**
 * How long the row that was just written to keeps its tint.
 *
 * Long enough to catch the eye that was on the pointer, short enough to be over
 * before it is read as a state the row is in. `prefers-reduced-motion` turns the
 * animation off in `portal.css`; the sentence in the strip is what carries the fact.
 */
const FLASH_MS = 300

/** What puts one action back. Anything it throws is said where the notice is. */
export type TaskUndo = () => Promise<unknown>

/**
 * An Undo's **first** write, and what the line says when the task has moved on.
 *
 * The Undo on this strip does not expire, and what it holds is how a task stood
 * *before* the action it reverses. So its first write carries `if_unchanged_since` —
 * the `updated_at` that action's own answer came back with — and the API makes the
 * change only if nothing has been written to the task in between
 * (`tasks/service.ts` `assertUnchangedSince`); otherwise it answers 409 `Conflict`
 * and changes nothing. That refusal is not a failure to report in the API's words
 * ("…has changed since it was read, so this change was not made. Read it again…"):
 * to somebody who pressed Undo it means one thing, and this is that sentence.
 *
 * Only the first write of a chain goes through here. A `Conflict` from a later one —
 * the claim that follows a release, taken by somebody else in between — is a
 * different fact about a task that *has* been partly put back. The claim's own words
 * say it ("…has already been given to somebody else"); the hand-back's are a tool
 * caller's, and `unlessTaken` says that one.
 *
 * `runUndo` has already taken the Undo off the line by the time this throws, which is
 * right for exactly this case: it could never succeed.
 */
export async function unlessChanged<T>(taskRef: string, write: () => Promise<T>): Promise<T> {
  try {
    return await write()
  } catch (problem) {
    if (isApiProblem(problem) && problem.status === 409 && problem.code === 'Conflict') {
      throw new NothingToUndo(taskRef, problem)
    }
    // The first write, and it was never sent: nothing of this Undo has been made.
    if (problem instanceof NotSent) NEVER_BEGUN.add(problem)
    throw problem
  }
}

/** The failures of an Undo's first write that was never sent (`unlessChanged`). */
const NEVER_BEGUN = new WeakSet<object>()

/** An Undo whose first write was refused: the task is not as the thing it undoes left it. */
class NothingToUndo extends Error {
  constructor(
    readonly taskRef: string,
    cause: unknown,
  ) {
    super(`${taskRef} has changed since, so there is nothing to undo.`, { cause })
  }
}

/**
 * What that is said as where the same Undo has **already been pressed once and did
 * not finish**. An Undo can be two writes — the give-back, then the name put on
 * again — and the first press may have made the first of them: answered, with the
 * second then not sent or not answered, or made and never answered itself. Either
 * way it is what refuses this press. "Nothing to undo" would be said of a task
 * sitting on the queue, half put back.
 */
function afterAnUnfinishedUndo(problem: NothingToUndo): string {
  return (
    `${problem.taskRef} has changed since — by your first Undo, which did not finish, or by ` +
    'somebody else — so nothing more was undone. Look at how the task stands now: it may be ' +
    'only partly put back.'
  )
}

/**
 * An Undo's **hand-back** — the name put on again after the give-back — and what the
 * line says when somebody took the task between the two.
 *
 * It is made from "nobody's, and open", which is what the give-back has just left
 * (`RowActions.tsx` `undoTake`), so a task taken in between is refused rather than
 * handed away from whoever took it. The API's sentence for that names the three
 * fields and tells a tool caller to read the task again; to somebody who pressed Undo
 * it is one fact about a task that is half put back.
 */
export function unlessTaken<T>(taskRef: string, write: () => Promise<T>): Promise<T> {
  return afterTheFirst(
    (what) =>
      `${taskRef} went back on the queue, and somebody else changed ${what} before it ` +
      'could be handed back. The task shows how it stands now.',
    write,
  )
}

/**
 * The **start** that follows a claim in one press, and what the line says when
 * somebody changed the task between the two. The claim was made and stands, so
 * "nothing was done" — what a move refused on its own says — would be untrue.
 */
export function unlessChangedBeforeStart<T>(taskRef: string, write: () => Promise<T>): Promise<T> {
  return afterTheFirst(
    (what) =>
      `${taskRef} was claimed, and somebody else changed ${what} before it could be ` +
      'started. The task shows how it stands now.',
    write,
  )
}

/**
 * A later step of one press: `said` is the line when somebody came between it and the
 * first, given **what** they changed in the words a sentence names it by
 * (`taskWords.ts` `changedWords`) — who holds it, where it stands. It is not always
 * that somebody took it: a task cancelled between the two steps is refused for where
 * it stands, and "took it" would be untrue.
 */
async function afterTheFirst<T>(
  said: (what: string) => string,
  write: () => Promise<T>,
): Promise<T> {
  try {
    return await write()
  } catch (problem) {
    const what = changedWords(problem)
    if (what !== null) throw new Error(said(what), { cause: problem })
    throw problem
  }
}

/** What happened, as the strip says it. */
export type TaskSaid = {
  readonly label: string
  /** The way back, when there is one. */
  readonly undo?: TaskUndo | undefined
  /** Which task it happened to, so the row can say so too. */
  readonly ref?: string | undefined
}

export type TaskNotice = {
  /** What just happened, and optionally the way back. Replaces the line. */
  readonly report: (said: TaskSaid) => void
  /** Why something was refused. Replaces the line. */
  readonly fail: (message: string) => void
  /** The reference of the row written to a moment ago, or null. */
  readonly flashed: string | null
}

type NoticeState = {
  readonly label: string
  readonly undo: TaskUndo | null
}

type TaskNoticeContext = TaskNotice & {
  readonly entry: NoticeState | null
  readonly undone: boolean
  readonly busy: boolean
  readonly error: string | null
  readonly runUndo: () => void
}

const NOWHERE: TaskNoticeContext = {
  report: () => {},
  fail: () => {},
  flashed: null,
  entry: null,
  undone: false,
  busy: false,
  error: null,
  runUndo: () => {},
}

const Ctx = createContext<TaskNoticeContext>(NOWHERE)

/** The page's one notice, for a row action or a cell edit to write to. */
export function useTaskNotice(): TaskNotice {
  return useContext(Ctx)
}

export function TaskNoticeProvider({ children }: { readonly children: ReactNode }) {
  const [entry, setEntry] = useState<NoticeState | null>(null)
  const [undone, setUndone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [flashed, setFlashed] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flash = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* A timer outliving the page would set state on something that is gone. */
  useEffect(() => {
    return () => {
      if (timer.current !== null) clearTimeout(timer.current)
      if (flash.current !== null) clearTimeout(flash.current)
    }
  }, [])

  const report = useCallback((said: TaskSaid): void => {
    if (timer.current !== null) clearTimeout(timer.current)
    setUndone(false)
    setError(null)
    setEntry({ label: said.label, undo: said.undo ?? null })
    if (said.ref === undefined) return
    if (flash.current !== null) clearTimeout(flash.current)
    setFlashed(said.ref)
    flash.current = setTimeout(() => {
      setFlashed(null)
    }, FLASH_MS)
  }, [])

  const fail = useCallback((message: string): void => {
    if (timer.current !== null) clearTimeout(timer.current)
    setEntry(null)
    setUndone(false)
    setError(message)
  }, [])

  /*
   * The line as it is right now, for the click handler to read.
   *
   * A `setEntry` updater cannot be where the undo runs: an updater is called during
   * render and may be called twice, and running a mutation there would send the
   * request twice under StrictMode.
   */
  const held = useRef<NoticeState | null>(null)
  useEffect(() => {
    held.current = entry
  }, [entry])
  /**
   * The Undos that were pressed once, **did not finish**, and were put back on the
   * line — less those of which nothing was sent at all (`NEVER_BEGUN`). Not "whose
   * last failure had no answer": a give-back that was answered and a hand-back that
   * was then never sent is an Undo half made, by a failure that made nothing.
   */
  const unfinished = useRef(new WeakSet<NoticeState>())

  const runUndo = useCallback((): void => {
    const current = held.current
    const undo = current === null ? null : current.undo
    if (undo === null) return
    held.current = null
    setEntry(null)
    setBusy(true)
    void undo()
      .then(() => {
        setUndone(true)
        setError(null)
        if (timer.current !== null) clearTimeout(timer.current)
        timer.current = setTimeout(() => {
          setUndone(false)
        }, UNDONE_MS)
      })
      .catch((problem: unknown) => {
        setError(
          problem instanceof NothingToUndo && current !== null && unfinished.current.has(current)
            ? afterAnUnfinishedUndo(problem)
            : messageOf(problem),
        )
        /*
         * A request that never arrived undid nothing, so the Undo is put back.
         *
         * It is taken off the line before it runs (above — a second press must not
         * send a second request), and it used to stay off whatever happened: a tab
         * that lost its connection for the second the reader pressed Undo lost the
         * only way back to the value they had just changed. A refusal is different —
         * the server answered, and the same press would be refused again — so only a
         * `NetworkError` restores it, and only if nothing has been said on the line
         * since. Pressed again it carries the same stamp, so if the first request did
         * land after all, the second is refused as "changed since" rather than
         * applied twice.
         */
        if (isNetworkError(problem) && current !== null) {
          // Some of it was made, perhaps: said so if it is pressed again and refused (above).
          if (!(problem instanceof NotSent && NEVER_BEGUN.has(problem))) {
            unfinished.current.add(current)
          }
          setEntry((now) => (now === null ? current : now))
        }
      })
      .finally(() => {
        setBusy(false)
      })
  }, [])

  const value = useMemo<TaskNoticeContext>(
    () => ({ report, fail, flashed, entry, undone, busy, error, runUndo }),
    [report, fail, flashed, entry, undone, busy, error, runUndo],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * The line, in the live region that is always there.
 *
 * `aria-live` on a wrapper that outlives its contents, rather than `role="status"`
 * on each `Notice`: a live region and its text arriving in the same commit is a
 * change several screen readers do not announce, and this is the one line on the
 * page whose whole job is to say what just happened. The visible strip is the same
 * markup it always was.
 */
export function TaskNoticeLine() {
  const notice = useContext(Ctx)

  return (
    <div className="tdock__say" aria-live="polite" aria-atomic="true">
      {notice.entry === null ? null : (
        <Notice>
          {notice.entry.label}{' '}
          {notice.entry.undo === null ? null : (
            <Button size="sm" onClick={notice.runUndo} disabled={notice.busy}>
              Undo
            </Button>
          )}
        </Notice>
      )}
      {notice.undone ? <Notice>Undone.</Notice> : null}
      {notice.error === null ? null : (
        <Notice need role="alert">
          {notice.error}
        </Notice>
      )}
    </div>
  )
}

/**
 * The strip at the bottom of the window: what just happened, and what is selected.
 *
 * Both used to be at the **top of the page**, above a table four thousand pixels
 * tall — the bulk bar with the count and every bulk action in it, and the line with
 * the Undo. Selecting a row at the bottom of the list therefore hid the thing that
 * said how many were selected, and an edit at scroll 3,072 put its own Undo 2,800px
 * out of sight. Neither is a control anybody can use where it was.
 *
 * `position: sticky; bottom: 0` rather than `fixed`, and last in the page's own
 * order: the strip is pinned to the bottom of the window while there is table below
 * it, and settles into the flow at the end of the page, where it covers nothing.
 */
export function TaskDock({ children }: { readonly children: ReactNode }) {
  return (
    <div className="tdock">
      <TaskNoticeLine />
      {children}
    </div>
  )
}
